import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { clock, dayAndClock } from '../lib/store-check.js';
import { todayStr } from '../lib/format.js';
import { StoreCheckCard } from './StoreCheckCard.jsx';
import { StoreCheckReports } from './StoreCheckReports.jsx';
import { StoreCheckCameras } from './StoreCheckCameras.jsx';

// ─── Operations → Store Check ──────────────────────────────────────────────
// One picture of every store at the same moment, with counts and what the AI
// flagged, for owners and managers (CJ 2026-10-07; cameras spec, Docket D358,
// D364). Stills only, never live video. Check now asks each store's Pi for a
// picture; it can take up to a minute to reach a store.

const WHY = { requested: 'asked for', scheduled: 'scheduled', rush: 'rush' };

export function StoreCheckView({ isOwner }) {
  const [tab, setTab] = useState('check');
  const [view, setView] = useState(null);
  const [day, setDay] = useState(() => todayStr());
  const [checks, setChecks] = useState([]);
  const [picked, setPicked] = useState('');          // '' = the newest check
  const [settings, setSettings] = useState(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');
  const pickedRef = useRef('');
  pickedRef.current = picked;

  // Refreshes in place: the cards stay on screen while the next answer loads.
  async function load() {
    try {
      const id = pickedRef.current;
      setView(await api('GET', id ? `/api/store-check/checks/${id}` : '/api/store-check/checks/latest'));
      setError('');
    } catch (e) { setError(e.message || 'Could not load Store Check'); }
  }
  async function loadDay(d) {
    try { setChecks((await api('GET', `/api/store-check/checks?day=${d}`)).checks); } catch (_) { setChecks([]); }
  }
  useEffect(() => { api('GET', '/api/store-check/settings').then(setSettings).catch(() => setSettings(null)); }, []);
  useEffect(() => { load(); }, [picked]);
  useEffect(() => { loadDay(day); }, [day]);

  // While a store is still being asked or a picture is being read, look again
  // every few seconds; otherwise once a minute.
  const busy = Boolean(view?.open) || JSON.stringify(view?.stores || []).includes('"state":"reading"');
  useEffect(() => {
    const t = setInterval(() => { load(); if (!busy) loadDay(day); }, busy ? 3000 : 60000);
    return () => clearInterval(t);
  }, [busy, day]);

  async function checkNow() {
    setAsking(true); setError('');
    try {
      await api('POST', '/api/store-check/checks');
      setDay(todayStr()); setPicked('');
      await load(); await loadDay(todayStr());
    } catch (e) { setError(e.message || 'Could not start a check'); }
    finally { setAsking(false); }
  }

  const tabs = [['check', 'Check'], ['report', 'Day report'], ['cameras', 'Cameras']];
  return (
    <div className="space-y-4">
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="text-base font-semibold">Store Check</div>
          <div className="text-xs text-gray-500">
            {view?.check
              ? `${dayAndClock(view.check.created_at)} · ${WHY[view.check.reason] || view.check.reason}${view.check.requested_by ? ` by ${view.check.requested_by}` : ''} · every store in the same minute`
              : 'A picture of every store at the same moment, with what the AI flagged.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} className={tab === id ? 'btn-primary' : 'btn-secondary'} style={{ fontSize: 12 }} aria-pressed={tab === id}>{label}</button>
          ))}
        </div>
      </div>

      {error && <div className="card" style={{ color: '#b00020' }}>Error: {error}</div>}
      {settings && !settings.ai_configured && (
        <div className="card text-sm" style={{ borderLeft: '4px solid #f9a825' }}>
          The AI reading is not set up yet, so pictures show without counts or statuses.
        </div>
      )}

      {tab === 'check' && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn-primary" onClick={checkNow} disabled={asking}>{asking ? 'Asking…' : 'Check now'}</button>
            <input type="date" value={day} max={todayStr()} aria-label="Day"
              onChange={(e) => { setDay(e.target.value || todayStr()); setPicked(''); }}
              style={{ fontSize: 12, padding: '6px 8px', border: '1px solid var(--rule, #ddd)', borderRadius: 6 }} />
            <select value={picked} onChange={(e) => setPicked(e.target.value)} aria-label="Earlier checks"
              style={{ fontSize: 12, padding: '6px 8px', border: '1px solid var(--rule, #ddd)', borderRadius: 6, background: '#fff' }}>
              <option value="">{day === todayStr() ? 'Latest check' : 'Pick a check'}</option>
              {[...checks].reverse().map((c) => <option key={c.id} value={c.id}>{clock(c.created_at)} · {WHY[c.reason] || c.reason}</option>)}
            </select>
            <span className="text-xs text-gray-500">{checks.length} check{checks.length === 1 ? '' : 's'} this day</span>
          </div>
          {settings && !settings.scheduled_enabled && (
            <div className="text-xs text-gray-500">
              Scheduled checks are off, so checks happen only when someone presses Check now.
              {isOwner ? ' Turn them on in Settings → Admin toggles once staff have been told in writing.' : ''}
            </div>
          )}
          {view && !view.check && <div className="card text-sm text-gray-500">No checks yet. Press Check now to ask every store for a picture.</div>}
          {view?.check && view.stores.length === 0 && <div className="card text-sm text-gray-500">No cameras are switched on. An owner can switch one on under Cameras.</div>}
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {(view?.stores || []).map((s) => <StoreCheckCard key={s.location_id} store={s} />)}
          </div>
          {view?.check && (
            <div className="text-xs text-gray-500">
              A picture shows one moment. “Nothing flagged” means nothing obvious was wrong in it, not that the area was checked.
              {settings ? ` Pictures are deleted after ${settings.keep_days} days.` : ''}
            </div>
          )}
        </>
      )}
      {tab === 'report' && <StoreCheckReports />}
      {tab === 'cameras' && <StoreCheckCameras isOwner={isOwner} />}
    </div>
  );
}
